import type React from 'react';

import type { BlockProps } from '@gitbook/slate-react';

import { BlockWrapper, getBlockLineHeight } from '../common';
import { useIsBlockHovered } from '../common/layout/hovered';
import { useStepIndex } from './BlockStepperContext';
import { InsertStepButton } from './InsertStepButton';
import { useBlockStepperPaletteFactory } from './palette';

// Size of the bullet in stepper
const BULLET_SIZE = 29;

/**
 * Component to render a stepper step block.
 */
export function BlockStepperStep(props: BlockProps): React.ReactElement {
    const { children, node, editor, parent } = props;
    const { readOnly } = editor;
    const isBlockHovered = useIsBlockHovered(node.key);

    const firstChild = node.nodes.first();
    const lineHeight = getBlockLineHeight(firstChild);
    const diff = -Math.round((lineHeight - BULLET_SIZE) / 2);

    const palette = useBlockStepperPaletteFactory({
        nodeKey: node.key,
        stepperBlockKey: parent.key,
    });

    return (
        <BlockWrapper {...props} palette={palette}>
            <div {...props.attributes} className="flex gap-4 md:gap-8">
                <div contentEditable={false} className="relative">
                    <div className="flex size-[calc(1.75rem+1px)] items-center justify-center rounded-full bg-muted text-secondary text-ui-heading-small tabular-nums">
                        <StepNumber nodeKey={node.key} />
                    </div>
                    <div className="absolute top-8 bottom-1 left-[0.875rem] w-px bg-muted" />
                    {!readOnly && (
                        <InsertStepButton
                            className="absolute bottom-0.5 left-[calc(0.25rem-1px)]"
                            visible={isBlockHovered}
                            stepperStepKey={node.key}
                        />
                    )}
                </div>
                <div className="flex-1 pb-6" style={{ marginTop: diff }}>
                    {children}
                </div>
            </div>
        </BlockWrapper>
    );
}

function StepNumber(props: { nodeKey: string }) {
    const index = useStepIndex(props.nodeKey);
    return <>{index + 1}</>;
}
